<template>
  <AppEventContainer>
      <!-- Rename Hand Modal -->
      <RenameHandModal
        v-model="showRenameModal"
        :initial-name="selectedParticipant?.name || ''"
        @save="saveHandName"
        @cancel="cancelRename"
      />

      <!-- Header -->
      <AppScreenTitle title="Manage Hands"/>

      <p class="text-normal">
        Rename your hands, initiate or check the status of hand transfers, and purchase other event add ons.
      </p>

      <!-- Extra Card Promo -->
      <ExtraCardMessage>
    </ExtraCardMessage>

      <!-- Hands List -->
      <div v-for="(registration, index) in feeRegistrations" :key="`hand-${index}`" >
        <div class="flex justify-between items-center">
          <h2 class="text-base font-bold text-normal-emphasis">
            {{ getParticipantName(registration) || `Hand ${index + 1}` }}           
          </h2>
          <AppOptionMenu v-if="showOptions(registration)" :menuItems="menuItems" @click="handleMenuAction($event, registration)" />
          <AppOptionMenu v-if="showRescindOptions(registration)" :menuItems="menuRescindItems" @click="handleMenuAction($event, registration)" />
        </div>
        <!-- Transfer Status Messages -->
        <div v-if="registration.status === 'accepted' && registration.ownerId !== registration.assignedToId" class="text-normal-emphasis italic mb-2">
          Transfer accepted by {{ registration.participantName }}.
        </div>
        <div v-if="registration.status === 'shared'" class="text-normal-emphasis italic mb-2">
          Waiting to be accepted by {{ registration.transferToName || registration.participantName }}.
        </div>

        <!-- Hand Cards -->
        <template v-if="getParticipant(registration)?.hand && getParticipant(registration)?.hand.cardSlots.length > 0">
          <HandPreview
            :hand="{
              cardSlots: getParticipant(registration)?.hand?.cardSlots || [],
              ranks: getParticipant(registration)?.hand?.ranks || []
            }"
            :extra-card="getParticipant(registration)?.extraCard || false"
          />
        </template>

        <!-- Transfer Actions -->
        <div v-if="registration.status === 'shared'" class="flex gap-4 ">
          <AppButton
            :is-small="true"
            title="Re-send text"
            @click="resendTransferText(registration.participantId)"
          />
          <AppButton
            :is-small="true"
            title="Cancel transfer"
            buttonType="Critical"
            @click="cancelTransfer(registration.participantId)"
          />
        </div>
      </div>

      <!-- Done Button -->
      <div class=" mb-4">
        <AppButton
          title="Done"
          class="w-full"
          @click="router.back()"
        />
      </div>
    <AppConfirmAction v-if="showCancelConfirm"
      :message="`Are you sure you want to cancel the transfer for '${registrationToCancel?.participantName}'?`" @confirm="confirmTransferCancel"
      @cancel="showCancelConfirm = false" />
    
    <!-- Add this new confirmation dialog -->
    <AppConfirmAction v-if="showRescindConfirm"
      :message="`Are you sure you want to rescind the transfer of '${registrationToRescind?.participantName}'?`" 
      button-title="Rescind transfer"
      @confirm="confirmRescindTransfer"
      @cancel="showRescindConfirm = false" />
  </AppEventContainer>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useEventParticipantStore } from '@/store/EventParticipant';
import { storeToRefs } from 'pinia';
import HandPreview from '@/components/HandPreview.vue';
import AppButton from '@/components/AppButton.vue';
import { EventParticipant } from '@models/EventParticipant';
import AppOptionMenu from '@/components/AppOptionMenu.vue';
import { MenuItem } from '@/models/MenuItem';
import AppScreenTitle from '@/components/AppScreenTitle.vue';
import RenameHandModal from '@/components/RenameHandModal.vue';
import ExtraCardMessage from '@/components/ExtraCardMessage.vue';
import { Registration } from '@models/Registration';
import RegistrationDataService from '@/services/RegistrationDataService';
import { useNotificationInjection } from '@/composables/useNotification';
import * as Sentry from "@sentry/vue";
import Constants from "@/models/Constants";

const router = useRouter();
const eventParticipantStore = useEventParticipantStore();
const { currentPokerRunEvent, currentEventParticipants, allRegistrations } = storeToRefs(eventParticipantStore);
const { renameParticipant, loadEventParticipant } = eventParticipantStore;
const showCancelConfirm = ref(false);
const registrationToCancel = ref<Registration | undefined>();
const notify = useNotificationInjection();
const selectedParticipant = ref<EventParticipant | null>(null);
const showRenameModal = ref(false);

// Add these new refs
const showRescindConfirm = ref(false);
const registrationToRescind = ref<Registration | undefined>();

const feeRegistrations = computed(() => {
  return allRegistrations.value.filter(x=> x.lineItems?.some(x=> x.itemType === Constants.FEE_TYPE));
});

// Function to get participant name from registration
const getParticipantName = (registration: Registration): string => {
  // If it's a transferred registration, use the participant name
  if (registration.status === 'accepted' && registration.ownerId !== registration.assignedToId) {
    return registration.participantName || '';
  }

  // Otherwise, try to find the associated participant to get its name
  const participant = getParticipant(registration);
  return participant?.name || '';
};

// Function to get participant from registration
const getParticipant = (registration: Registration): EventParticipant | undefined => {
  return currentEventParticipants.value.find(p => p.id === registration.participantId);
};


const showOptions = (registration: Registration) => {
  return registration.status !== 'shared' && registration.ownerId === registration.assignedToId;
};

// Function to determine if options should be shown for a registration
const showRescindOptions = (registration: Registration): boolean => {
  // Show options if the registration is not shared and not accepted by someone else
  return registration.status === 'accepted' && registration.ownerId !== registration.assignedToId;
};

// Menu items for the option menu
const menuItems: MenuItem[] = [
  {
    label: 'Rename Hand',
    action: 'rename'
  },
  {
    label: 'Transfer Hand',
    action: 'transfer'
  }
];

const menuRescindItems: MenuItem[] = [
  {
    label: 'Rescind transfer',
    className:'text-critical-text',
    action: 'rescindTransfer'
  }
];

// Function to handle menu actions
const handleMenuAction = (action: string, registration: Registration) => {
  if (action === 'rename') {
    renameHand(registration);
  } else if (action === 'transfer') {
    transferHand(registration);
  }
  else if (action === 'rescindTransfer') {
    rescindTransfer(registration);
  }
};

// Function to rename hand
const renameHand = (registration: Registration) => {
  const participant = getParticipant(registration);
  if (participant) {
    selectedParticipant.value = participant;
    showRenameModal.value = true;
  }
};

// Function to save hand name
const saveHandName = async (newName: string) => {
  if (selectedParticipant.value && newName.trim()) {
    try {
      // Call the store method to update the name in the database
      await renameParticipant(selectedParticipant.value, newName);
      console.log(`Successfully renamed hand ${selectedParticipant.value.id} to ${newName}`);
    } catch (error) {
      console.error('Error renaming hand:', error);
    }
  }
  showRenameModal.value = false;
  selectedParticipant.value = null;
};

// Function to cancel rename
const cancelRename = () => {
  showRenameModal.value = false;
  selectedParticipant.value = null;
};

const rescindTransfer = async (registration: Registration) => {
  registrationToRescind.value = registration;
  showRescindConfirm.value = true;
};

const confirmRescindTransfer = async () => {
  try {
    if (registrationToRescind.value) {
      showRescindConfirm.value = false;
      await RegistrationDataService.cancelAssignment(registrationToRescind.value);
      loadEventParticipant(currentPokerRunEvent.value.id, registrationToRescind.value.ownerId);
      notify.success("Transfer rescinded successfully");
    }
  } catch (error: any) {
    Sentry.captureException(error);
    notify.error("Error rescinding transfer", error?.message);
  } finally {
    showRescindConfirm.value = false;
    registrationToRescind.value = undefined;
  }
};

// Function to transfer hand
const transferHand = (registration: Registration) => {
  console.log('Transferring hand:', registration.participantId);
  // Navigate to the AssignRegistration page for this hand
  router.push({
    name: 'AssignRegistration',
    params: {
      eventId: currentPokerRunEvent.value?.id || '',
      id: registration.id || ''
    }
  });
};

// Function to resend transfer text
const resendTransferText = (participantId: string | undefined) => {
  if (!participantId) return;

  try {
    const registration = allRegistrations.value.find((reg) => reg.participantId === participantId);
    if(!registration) {
      return;
    }

    const data = {
    text: `To redeem your registration for the ${currentPokerRunEvent.value?.name} event:

1. Install the Aces Poker Run App from the App Store.
2. Login to the app.
3. Click on the link.

${registration.redemptionLink}

Thanks`,
    title: `Claim your ${currentPokerRunEvent.value?.name} registration!`,
  };
  window.navigator.share(data);
  } catch (error: any) {
    Sentry.captureException(error);
    notify.error("Error assigning registration", error?.message);
  }
};

// Function to cancel transfer
const cancelTransfer = async (participantId: string | undefined) => {
  if (!participantId) return;

  try {
    registrationToCancel.value = allRegistrations.value.find((reg) => reg.participantId === participantId);
    showCancelConfirm.value = true;
  } catch (error: any) {
    Sentry.captureException(error);
    notify.error("Error cancel sharing", error?.message);
  }
};

const confirmTransferCancel = async () => {
  try {
    const registration = registrationToCancel.value;
    if (registration) {
      await RegistrationDataService.cancelAssignment(registration);
      loadEventParticipant(currentPokerRunEvent.value.id, registration.ownerId);
    }
    showCancelConfirm.value = false;
  } catch (error: any) {
    Sentry.captureException(error);
    notify.error("Error cancel sharing", error?.message);
  }
};

</script>
